import { useParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { CheckCircle2, XCircle, Upload } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '../hooks/use-toast';

// Mock product data - will be replaced with API call
const mockProduct = {
  id: '1',
  name: 'Raspberry & Yuzu',
  brand: 'Tenzing',
  category: 'beverage',
  status: 'pending' as 'pending' | 'approved' | 'rejected',
  confidence: 0.85,
  nutrition: {
    per100g: {
      energy: 73,
      fat: 0.4,
      carbs: 14.0,
      sugars: 1.6,
      protein: 3.4,
      salt: 0.02,
    },
  },
  allergens: [],
  ingredients: 'Water, Raspberry Juice, Yuzu Extract, Natural Flavors',
  sourceUrl: 'https://www.tesco.com/...',
};

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
        toast({
          title: 'Invalid file type',
          description: 'Please upload a PNG or JPG image',
          variant: 'destructive',
        });
        return;
      }
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    // TODO: Call API to upload and sync to Google Sheets
    toast({
      title: 'Success',
      description: 'Product image uploaded and synced to Google Sheets',
    });
  };

  const handleApprove = () => {
    // TODO: Call API
    toast({
      title: 'Approved',
      description: 'Product has been approved',
    });
  };

  const handleReject = () => {
    // TODO: Call API
    toast({
      title: 'Rejected',
      description: 'Product has been rejected',
      variant: 'destructive',
    });
  };

  return (
    <DashboardLayout>
      <PageHeader
        title={mockProduct.name}
        description={`${mockProduct.brand} • ${mockProduct.category}`}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleReject}>
              <XCircle className="mr-2 h-4 w-4" />
              Reject
            </Button>
            <Button onClick={handleApprove}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Approve
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex items-center gap-4">
        <Badge variant={mockProduct.status === 'approved' ? 'default' : mockProduct.status === 'rejected' ? 'destructive' : 'secondary'}>
          {mockProduct.status.charAt(0).toUpperCase() + mockProduct.status.slice(1)}
        </Badge>
        <Badge variant={mockProduct.confidence > 0.7 ? 'default' : 'secondary'}>
          Confidence: {(mockProduct.confidence * 100).toFixed(0)}%
        </Badge>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="nutrition">Nutrition</TabsTrigger>
          <TabsTrigger value="ingredients">Ingredients</TabsTrigger>
          <TabsTrigger value="assets">Assets</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Product Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Brand</p>
                <p className="text-lg">{mockProduct.brand}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Category</p>
                <p className="text-lg">{mockProduct.category}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Source</p>
                <a
                  href={mockProduct.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  View Source
                </a>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="nutrition" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Nutrition Information</CardTitle>
              <CardDescription>Per 100g</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Energy</p>
                  <p className="text-lg font-semibold">{mockProduct.nutrition.per100g.energy} kcal</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fat</p>
                  <p className="text-lg font-semibold">{mockProduct.nutrition.per100g.fat}g</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Carbohydrates</p>
                  <p className="text-lg font-semibold">{mockProduct.nutrition.per100g.carbs}g</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Sugars</p>
                  <p className="text-lg font-semibold">{mockProduct.nutrition.per100g.sugars}g</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Protein</p>
                  <p className="text-lg font-semibold">{mockProduct.nutrition.per100g.protein}g</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Salt</p>
                  <p className="text-lg font-semibold">{mockProduct.nutrition.per100g.salt}g</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ingredients" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Ingredients</CardTitle>
            </CardHeader>
            <CardContent>
              <p>{mockProduct.ingredients}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Allergens</CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                {mockProduct.allergens.length === 0
                  ? 'Contains: no major allergens'
                  : `Contains: ${mockProduct.allergens.join(', ')}`}
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assets" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Product Image</CardTitle>
              <CardDescription>Upload PNG or JPG image</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleFileChange}
                  className="cursor-pointer"
                />
                {preview && (
                  <img
                    src={preview}
                    alt="Preview"
                    className="h-32 w-32 rounded-md object-cover"
                  />
                )}
              </div>
              {selectedFile && (
                <Button onClick={handleUpload}>
                  <Upload className="mr-2 h-4 w-4" />
                  Upload & Sync to Google Sheets
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

